import { useAppContext } from "@/context/AppContext";
import { useAuthContext } from "@/context/AuthContext";
import { useContentSelectieContext } from "@/context/ContentSelectieContext";
import { useCallback, useMemo } from "react";

export default function ProfielBreadCrumbsV2() {
  const {
    selectedHoofdstuk,
    selectedEdition,
    selectedNiveau,
    selectedMethode,
    selectedJaar,
    setSelectedEdition,
    setSelectedNiveau,
    setSelectedWiskundeType,
    setSelectedMethode,
    setSelectedJaar,
    deselectHoofdstuk
  } = useContentSelectieContext();

  const handleStepBack = useCallback(() => {
    if (selectedHoofdstuk) {
      deselectHoofdstuk();
      return;
    }

    if (selectedJaar && selectedMethode && selectedNiveau && selectedEdition) {
      setSelectedEdition(undefined);
      return;
    }

    if (selectedJaar && selectedMethode && selectedNiveau) {
      setSelectedNiveau(undefined);
      setSelectedWiskundeType(undefined);
      return;
    }

    if (selectedJaar && selectedMethode) {
      setSelectedMethode(undefined);
      setSelectedEdition(undefined);
      return;
    }

    if (selectedJaar) {
      setSelectedJaar(undefined);
    }
  }, [
    selectedHoofdstuk,
    selectedEdition,
    selectedNiveau,
    selectedMethode,
    selectedJaar,
    deselectHoofdstuk,
    setSelectedEdition,
    setSelectedNiveau,
    setSelectedWiskundeType,
    setSelectedMethode,
    setSelectedJaar
  ]);

  const label = useProfileLabel();

  if (!label) return null;

  return (
    <div className="mx-auto flex max-w-xl flex-wrap items-center justify-center gap-2">
      <button
        type="button"
        onClick={handleStepBack}
        className="inline-flex h-9 flex-none items-center gap-2 rounded-full border border-gray-200 bg-white px-3 text-sm font-semibold text-gray-600 shadow-sm transition-colors hover:border-info/30 hover:bg-info/5 hover:text-info focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-info/20"
        aria-label="Ga een stap terug in de profielselectie"
      >
        <svg
          className="h-4 w-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 18l-6-6 6-6"
          />
        </svg>
        Vorige stap
      </button>

      <div className="inline-flex min-w-0 items-center gap-2.5 rounded-full border border-gray-200 bg-white px-4 py-2 shadow-sm">
        <svg
          className="h-4 w-4 flex-shrink-0 text-info"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
          />
        </svg>
        <span className="min-w-0 truncate text-sm text-gray-500">
          {label}
        </span>
      </div>
    </div>
  );
}

function useProfileLabel(): string {
  const { hoofdstukken } = useAppContext();
  const { isPremium } = useAuthContext();
  const {
    selectedJaar,
    selectedMethode,
    selectedNiveau,
    selectedEdition,
    selectedWiskundeType,
    selectedHoofdstuk
  } = useContentSelectieContext();

  const hoofdstukLabel = useMemo(() => {
    if (!isPremium || !selectedHoofdstuk) return undefined;
    const h = hoofdstukken.find((h) => h.id === selectedHoofdstuk);
    if (!h) return undefined;
    const match = h.titel.match(/^H(\d+)/);
    return match ? `Hoofdstuk ${match[1]}` : h.titel;
  }, [isPremium, hoofdstukken, selectedHoofdstuk]);

  const parts: string[] = [];

  if (selectedJaar) parts.push(`Klas ${selectedJaar}`);
  if (selectedMethode) parts.push(selectedMethode);
  if (selectedNiveau) {
    const niveauLabel = selectedWiskundeType
      ? `${selectedNiveau} ${selectedWiskundeType}`
      : selectedNiveau;
    parts.push(niveauLabel);
  }
  if (selectedEdition) parts.push(`Editie ${selectedEdition}`);

  if (hoofdstukLabel) parts.push(hoofdstukLabel);

  return parts.join(" · ");
}
